const pickFirstString = (...values) => (
  values.find((value) => typeof value === 'string' && value.trim()) || ''
);

export const normalizePartLabel = (value) => {
  const text = value == null ? '' : String(value).trim();
  return text;
};

export const resolveEventPartFallbackLabel = (...values) => {
  const normalized = values
    .map((value) => normalizePartLabel(value))
    .find(Boolean);
  return normalized || '未設定';
};

export const normalizeEventUuidList = (values) => {
  const source = Array.isArray(values) ? values : [values];
  const seen = new Set();
  const out = [];
  source.forEach((value) => {
    const normalized = value == null ? '' : String(value).trim();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    out.push(normalized);
  });
  return out;
};

export const normalizeProfileEventPartAssignments = (assignments) => {
  if (!Array.isArray(assignments)) return [];
  return assignments
    .map((assignment) => {
      const eventUuid = String(assignment?.event_uuid || assignment?.eventUuid || '').trim();
      if (!eventUuid) return null;
      const partLabel = normalizePartLabel(
        pickFirstString(assignment?.part_label, assignment?.partLabel, assignment?.part, assignment?.label)
      );
      const partSortRaw = assignment?.part_sort_order ?? assignment?.partSortOrder;
      const partSortOrder = Number.isFinite(Number(partSortRaw)) ? Number(partSortRaw) : null;
      const castingInfoId = assignment?.casting_info_id ?? assignment?.castingInfoId ?? null;
      const hasAssignment = assignment?.has_assignment === true
        || assignment?.hasAssignment === true
        || castingInfoId != null
        || Boolean(partLabel);
      return {
        eventUuid,
        partLabel,
        hasAssignment,
        partSortOrder,
        castingInfoId,
      };
    })
    .filter(Boolean);
};

export const buildProfileEventPartEntries = ({
  isUnifiedEventGroupChat = false,
  eventUuids = [],
  groupEvents = [],
  eventPartAssignments = [],
  fallbackPartLabel = '',
} = {}) => {
  if (!isUnifiedEventGroupChat) return [];

  const groupList = Array.isArray(groupEvents) ? groupEvents : [];
  const eventOrder = new Map();
  groupList.forEach((event, index) => {
    const eventUuid = String(event?.event_uuid || event?.eventUuid || '').trim();
    if (!eventUuid || eventOrder.has(eventUuid)) return;
    eventOrder.set(eventUuid, index);
  });

  const assignmentMap = new Map();
  normalizeProfileEventPartAssignments(eventPartAssignments).forEach((assignment) => {
    assignmentMap.set(assignment.eventUuid, assignment);
  });

  let targets = normalizeEventUuidList(eventUuids);
  if (!targets.length) {
    targets = Array.from(assignmentMap.keys());
  }
  if (eventOrder.size > 0) {
    targets = targets.filter((eventUuid) => eventOrder.has(eventUuid));
  }
  targets = [...targets].sort((a, b) => {
    const orderA = eventOrder.has(a) ? eventOrder.get(a) : Number.MAX_SAFE_INTEGER;
    const orderB = eventOrder.has(b) ? eventOrder.get(b) : Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    return a.localeCompare(b, 'en');
  });

  const resolvedFallbackPartLabel = resolveEventPartFallbackLabel(fallbackPartLabel);

  return targets.map((eventUuid) => {
    const event = eventOrder.has(eventUuid) ? groupList[eventOrder.get(eventUuid)] : null;
    const eventLabel = pickFirstString(event?.performance_type, event?.name) || eventUuid;
    const assignment = assignmentMap.get(eventUuid);
    const assignedPartLabel = normalizePartLabel(assignment?.partLabel);
    const partLabel = assignedPartLabel || resolvedFallbackPartLabel;
    return {
      eventUuid,
      eventLabel,
      partLabel,
      hasAssignment: assignment?.hasAssignment === true,
    };
  });
};

export const buildProfileEventPartDisplay = ({
  isUnifiedEventGroupChat = false,
  eventUuids = [],
  groupEvents = [],
  eventPartAssignments = [],
  fallbackPartLabel = '',
} = {}) => {
  const entries = buildProfileEventPartEntries({
    isUnifiedEventGroupChat,
    eventUuids,
    groupEvents,
    eventPartAssignments,
    fallbackPartLabel,
  });
  if (entries.length <= 1) {
    return {
      entries,
      shouldShowEventPartBlock: false,
    };
  }

  const distinctPartLabels = new Set(
    entries
      .map((entry) => normalizePartLabel(entry?.partLabel))
      .filter(Boolean)
  );

  return {
    entries,
    shouldShowEventPartBlock: distinctPartLabels.size > 1,
  };
};
